import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Tooltip, IconButton } from "@mui/material";
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiRef,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import Pagination from "@mui/material/Pagination";
import DownloadingIcon from "@mui/icons-material/Downloading";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import AlertManager from "../../../shared/Alert/Alert";
import { downloadRunDetailsReport } from "../../../API/runDetailsReportDownloadApi";
import { updateLandedCostItemWorkflow } from "../../../API/landedCostUpdateApi";
import classes from "../../LandedCostUpload/LandedCostUpload.module.css";

const formatDateParts = (value) => {
  if (Array.isArray(value)) {
    const [year, month, day, hour, minute, second] = value;
    if (year && month && day) {
      const datePart = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      if (hour !== undefined) {
        return `${datePart} ${String(hour ?? 0).padStart(2, "0")}:${String(minute ?? 0).padStart(2, "0")}:${String(
          second ?? 0
        ).padStart(2, "0")}`;
      }
      return datePart;
    }
  }
  if (value instanceof Date) {
    return value.toISOString().slice(0, 19).replace("T", " ");
  }
  if (value === 0) {
    return "0";
  }
  return value ?? "";
};

const pickValue = (row, keys, fallback) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return fallback;
};

const resolveBatchId = (row, fallback) =>
  pickValue(row, ["batchId", "batch_id", "batchid"], fallback ?? "");

export const resolveCjwFilename = (row) =>
  pickValue(row, ["cjwFilename", "cjwFileName", "cjw_filename"], "");

const resolveRowId = (row, index, batchId) =>
  pickValue(
    row,
    ["id", "rowId", "row_id", "uuid", "workflowId", "workflow_id"],
    batchId ? `${batchId}-${index + 1}` : index + 1
  );

const normalizeDecimalValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  const parsedValue = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const EDIT_NEW_LANDED_COST_MIN = -100000;
const EDIT_NEW_LANDED_COST_MAX = 100000;

export const isEditNewLandedCostWithinRange = (value) => {
  const numericValue = normalizeDecimalValue(value);
  if (numericValue === null) {
    return true;
  }

  return numericValue >= EDIT_NEW_LANDED_COST_MIN && numericValue <= EDIT_NEW_LANDED_COST_MAX;
};

const DecimalEditCell = (params) => {
  const { id, field, value } = params;
  const [inputValue, setInputValue] = useState(value ?? "");

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  const handleChange = (event) => {
    const rawValue = event.target.value;
    setInputValue(rawValue);
    params.api.setEditCellValue({ id, field, value: rawValue }, event);
  };

  return (
    <input
      type="text"
      step="0.01"
      inputMode="decimal"
      min={EDIT_NEW_LANDED_COST_MIN}
      max={EDIT_NEW_LANDED_COST_MAX}
      value={inputValue}
      onChange={handleChange}
      style={{ width: "100%", height: "100%", boxSizing: "border-box", padding: "6px" }}
      data-testid={`${field}-edit-${id}`}
    />
  );
};

const isTruthyFlag = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return ["true", "1", "y", "yes"].includes(normalized);
  }
  return Boolean(value);
};

const paramsMissingKeys = ["areParamsMissing", "are_params_missing", "paramMissing", "param_missing", "parammissing"];
const skuExcludedKeys = ["skuExcluded", "sku_excluded", "isSkuExcluded", "is_sku_excluded"];

const resolveParamsMissingValue = (row) => {
  let hasAnyValue = false;

  for (const key of paramsMissingKeys) {
    const value = row?.[key];
    if (value === undefined || value === null || value === "") {
      continue;
    }

    hasAnyValue = true;
    if (isTruthyFlag(value)) {
      return true;
    }
  }

  return hasAnyValue ? false : null;
};

export const hasMissingParams = (row) => resolveParamsMissingValue(row) === true;

const resolveSkuExcludedValue = (row) => {
  for (const key of skuExcludedKeys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return null;
};

export const isSkuExcluded = (row) => isTruthyFlag(resolveSkuExcludedValue(row));

const resolveApprovalState = (row) => {
  const value = pickValue(
    row,
    ["approvalStatus", "approval_status", "approved", "approvedStatus", "approved_status", "isApproved"],
    null
  );
  if (value === undefined || value === null || value === "") {
    return null;
  }

  if (typeof value === "boolean") {
    return value ? "approved" : null;
  }

  if (typeof value === "number") {
    if (value === 1) {
      return "approved";
    }
    return null;
  }

  return String(value).trim().toLowerCase();
};

export const isFinalApprovalState = (row) => {
  const state = resolveApprovalState(row);
  return state === "approved" || state === "rejected";
};

const canEditLandedCost = (row) => {
  return !hasMissingParams(row) && !isSkuExcluded(row);
};

export const hasEditableRows = (rows = []) => Array.isArray(rows) && rows.some((row) => canEditLandedCost(row));

const ActionButtonWithTooltip = ({ title, disabled, children, buttonProps, buttonClassName }) => (
  <Tooltip title={disabled ? title : ""} disableHoverListener={!disabled} disableFocusListener={!disabled} disableTouchListener={!disabled}>
    <span style={{ display: "inline-flex" }}>
      <Button {...buttonProps} className={buttonClassName} disabled={disabled}>
        {children}
      </Button>
    </span>
  </Tooltip>
);

const isNewSkuStatus = (value) => String(value ?? "").trim().toLowerCase() === "new";

const isClosedStatus = (value) => String(value ?? "").trim().toLowerCase() === "closed";

const resolveTotalNoOfSkus = (row) => {
  const value = pickValue(row, ["totalNoOfSkus", "totalSkus", "totalSku", "total_no_of_skus", "total_skus", "total_sku"], null);
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsedValue = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const isRunDetailsReportDisabled = (row) => {
  const totalNoOfSkus = resolveTotalNoOfSkus(row);
  const status = pickValue(row, ["status", "statusName", "status_name"], "");

  return totalNoOfSkus === 0 || isClosedStatus(status);
};

const RunDetailsTable = ({
  runDetails = [],
  loading,
  showUploadButton = false,
  onUploadClick,
  showActions = false,
  onBatchNavigate,
  onRefreshData,
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  variant = "detail",
  selectedBatchId,
  page,
  onPageChange,
}) => {
  const [rows, setRows] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitArmed, setSubmitArmed] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const normalizedInput = useMemo(
    () => (Array.isArray(runDetails) ? runDetails : []),
    [runDetails]
  );
  const dirtyRowsRef = useRef(new Map());
  const apiRef = useGridApiRef();

  useEffect(() => {
    if (variant === "detail") {
      setSelectionModel([]);
    }
  }, [normalizedInput, selectedBatchId, variant]);

  const selectedRows = useMemo(() => {
    if (!selectionModel.length) {
      return [];
    }
    const selectedIds = new Set(selectionModel.map((value) => String(value)));
    return rows.filter((row) => selectedIds.has(String(row.id)));
  }, [rows, selectionModel]);

  const selectedRowsHaveMissingParams = useMemo(
    () => selectedRows.some((row) => hasMissingParams(row)),
    [selectedRows]
  );

  const selectedRowsHaveFinalApprovalState = useMemo(
    () => selectedRows.some((row) => isFinalApprovalState(row)),
    [selectedRows]
  );

  const selectedRowsHaveSkuExcluded = useMemo(
    () => selectedRows.some((row) => isSkuExcluded(row)),
    [selectedRows]
  );

  const canEditAnyRow = useMemo(() => hasEditableRows(rows), [rows]);

  const approvalActionDisabled =
    selectedRowsHaveMissingParams || selectedRowsHaveFinalApprovalState || selectedRowsHaveSkuExcluded;

  const getApprovalActionTooltip = () => {
    if (isEditing) {
      return "Finish editing before approving or rejecting.";
    }
    if (!selectedRows.length) {
      return "Select at least one row.";
    }
    if (approvalActionDisabled) {
      return "Selected rows are missing parameters, SKU-excluded, or already finalized.";
    }
    return "";
  };

  const getEditButtonTooltip = () => {
    if (!canEditAnyRow) {
      return "No editable rows are available.";
    }
    if (saving) {
      return "Saving in progress.";
    }
    if (isEditing && !submitArmed) {
      return "Finish editing before saving changes.";
    }
    return "";
  };

  const handleReportDownload = async (batchId, type) => {
    if (!batchId) {
      AlertManager.error("Missing batch ID for report download.", { contactSupport: false });
      return;
    }
    try {
      await downloadRunDetailsReport(batchId, type);
    } catch (error) {
      AlertManager.error(error?.message || "Unable to download report.", { contactSupport: false });
    }
  };

  const columns = useMemo(
    () => {
      if (variant === "summary") {
        return [
          {
            field: "batchId",
            headerName: "Batch ID",
            flex: 0.9,
            minWidth: 90,
            editable: true,
            renderCell: (params) =>
              params?.value ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ textTransform: "none", fontWeight: 600, padding: 0, minWidth: "auto" }}
                  onClick={() => onBatchNavigate && onBatchNavigate(params.value, params.row)}
                >
                  {params.value}
                </Button>
              ) : (
                "-"
              ),
          },
          { field: "batchType", headerName: "Batch Type", flex: 0.9, minWidth: 95, editable: true },
          {
            field: "cjwFilename",
            headerName: "CJW Filename",
            flex: 1.2,
            minWidth: 180,
            renderCell: ({ value }) => value || "-",
          },
          {
            field: "totalNoOfSkus",
            headerName: "Total # of SKUs",
            flex: 0.9,
            minWidth: 90,
            editable: true,
          },
          {
            field: "totalOldLandedAmt",
            headerName: "Total Old Landed Amount",
            flex: 0.95,
            minWidth: 105,
            editable: true,
          },
          {
            field: "totalNewLandedAmt",
            headerName: "Total New Landed Amount",
            flex: 0.95,
            minWidth: 105,
            editable: true,
          },
          {
            field: "totalPercentChgLandedCost",
            headerName: "Total % Change Landed Cost",
            flex: 1,
            minWidth: 110,
            editable: true,
          },
          { field: "status", headerName: "Status", flex: 0.65, minWidth: 80, editable: true },
          { field: "skuStatus", headerName: "SKU Status", flex: 0.8, minWidth: 95, editable: true },
          { field: "createDate", headerName: "Last updated date", flex: 0.95, minWidth: 120, editable: true },
          {
            field: "downloadReports",
            headerName: "Download reports",
            flex: 1.1,
            minWidth: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
              const { id } = params.row;
              const disableReports = isRunDetailsReportDisabled(params?.row);
              const disableReason = disableReports
                ? "Reports are unavailable for a closed batch or batch with zero SKUs."
                : "";
              const buttons = [
                {
                  key: "RECALCULATED_LANDED_COST",
                  tooltip: "Relanding report",
                  Icon: DownloadingIcon,
                },
                {
                  key: "DATA_COMPARISON",
                  tooltip: "Data comparison report",
                  Icon: DownloadingIcon,
                },
                {
                  key: "EXCLUSION",
                  tooltip: "Exclusion report",
                  Icon: DownloadForOfflineIcon,
                },
              ];

              return (
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  {buttons.map(({ key, tooltip, Icon }) => (
                    <Tooltip key={`${id}-${key}`} title={disableReason || tooltip}>
                      <span>
                      <IconButton
                        size="small"
                        onClick={() => handleReportDownload(params?.row?.batchId, key)}
                        disabled={disableReports}
                        sx={{ p: 0.25, color: disableReports ? "#bdbdbd" : "#000" }}
                      >
                          <Icon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  ))}
                </Box>
              );
            },
          },
        ];
      }

      const detailColumns = [
        {
          field: "batchId",
          headerName: "Batch ID",
          flex: 1.1,
          minWidth: 150,
          renderCell: ({ value }) => (value ? String(value) : "-"),
        },
        {
          field: "parsedSku",
          headerName: "Parsed SKU",
          flex: 1,
          minWidth: 140,
        },
        { field: "hqLine", headerName: "HQ Line", flex: 0.8, minWidth: 120 },
        { field: "fieldLine", headerName: "Field Line", flex: 0.9, minWidth: 140 },
        {
          field: "oldLandedCost",
          headerName: "Old Landed Cost",
          flex: 1,
          minWidth: 150,
        },
        {
          field: "newLandedCost",
          headerName: "New Landed Cost",
          flex: 1,
          minWidth: 150,
        },
        {
          field: "editNewLandedCost",
          headerName: "Modified New Landed Cost",
          flex: 1,
          minWidth: 160,
          valueGetter: (params) => params?.row?.editNewLandedCost ?? params?.row?.newLandedCost ?? null,
          valueParser: (value, params) => {
            const normalizedValue = normalizeDecimalValue(value);
            if (normalizedValue !== null) {
              return normalizedValue;
            }
            return value;
          },
          renderEditCell: (params) => <DecimalEditCell {...params} />,
        },
        {
          field: "percentChgLandedCost",
          headerName: "% Change Landed Cost",
          flex: 1.2,
          minWidth: 180,
        },
        {
          field: "editPercentChgLandedCost",
          headerName: "Modified % change Landed Cost",
          flex: 1.2,
          minWidth: 190,
          valueGetter: (params) => params?.row?.editPercentChgLandedCost ?? params?.row?.percentChgLandedCost ?? null,
        },
        {
          field: "asgVendorAccountNumber",
          headerName: "ASG Vendor Account #",
          flex: 1.2,
          minWidth: 200,
        },
        {
          field: "asgVendorName",
          headerName: "ASG Vendor Name",
          flex: 1.3,
          minWidth: 220,
        },
        {
          field: "asgVendorGroupCode",
          headerName: "ASG Vendor Group Code",
          flex: 1,
          minWidth: 180,
        },
        {
          field: "asgVendorProductNumber",
          headerName: "ASG Vendor Product #",
          flex: 1.1,
          minWidth: 190,
        },
        {
          field: "asgPrimaryVendorAccountNumber",
          headerName: "ASG Primary Vendor Account #",
          flex: 1.4,
          minWidth: 230,
        },
        {
          field: "productLifeCycleStateName",
          headerName: "Lifecycle State",
          flex: 1,
          minWidth: 160,
        },
        {
          field: "countryOfOrigin",
          headerName: "Country of Origin",
          flex: 0.9,
          minWidth: 150,
        },
        { field: "fobCostUnit", headerName: "FOB Cost Unit", flex: 0.9, minWidth: 140 },
        { field: "unitCostDate", headerName: "Unit Cost Date", flex: 1, minWidth: 160 },
        { field: "palletQuantity", headerName: "Pallet Qty", flex: 0.8, minWidth: 110 },
        { field: "masterCartonQuantity", headerName: "Master Carton Qty", flex: 1, minWidth: 150 },
        { field: "oldFobCost", headerName: "Old FOB Cost", flex: 1, minWidth: 140 },
        { field: "newFobCost", headerName: "New FOB Cost", flex: 1, minWidth: 140 },
        { field: "tariffAmt", headerName: "Tariff Amount", flex: 1, minWidth: 140 },
        { field: "freightAmt", headerName: "Freight Amount", flex: 1, minWidth: 140 },
        { field: "issCommissionAmt", headerName: "ISS Commission Amount", flex: 1.1, minWidth: 180 },
        { field: "landedCostVariability", headerName: "Landed Cost Variability", flex: 1.2, minWidth: 200 },
        { field: "variabilityPercent", headerName: "Variability %", flex: 1, minWidth: 140 },
        { field: "adjustmentsRate", headerName: "Antidumping Rate", flex: 1, minWidth: 150 },
        { field: "couterVailingRate", headerName: "Countervailing Rate", flex: 1.2, minWidth: 180 },
        { field: "adjustmentsAmt", headerName: "Antidumping Amount", flex: 1.2, minWidth: 180 },
        { field: "couterVailingAmt", headerName: "Countervailing Amount", flex: 1.2, minWidth: 190 },
        { field: "containerPalletAmt", headerName: "Container Pallet Amount", flex: 1.3, minWidth: 210 },
        { field: "poStatusValue", headerName: "PO Status", flex: 0.9, minWidth: 140 },
        { field: "poProductNumber", headerName: "PO Product Number", flex: 1.2, minWidth: 190 },
        { field: "areParamsMissing", headerName: "Params Missing", flex: 1, minWidth: 150 },
        { field: "approvedDate", headerName: "Approved Date", flex: 1, minWidth: 160 },
        { field: "approvedBy", headerName: "Approved By", flex: 1, minWidth: 150 },
        { field: "nextLcCalcDate", headerName: "Next LC Calc Date", flex: 1.2, minWidth: 180 },
        { field: "repricingStatus", headerName: "Repricing Status", flex: 1, minWidth: 160 },
        { field: "eventType", headerName: "Event Type", flex: 0.9, minWidth: 140 },
        { field: "createDate", headerName: "Last updated date", flex: 1, minWidth: 170 },
        { field: "createdBy", headerName: "Created By", flex: 1, minWidth: 140 },
        { field: "updateDate", headerName: "Updated Date", flex: 1, minWidth: 170 },
        { field: "updateBy", headerName: "Updated By", flex: 1, minWidth: 140 },
        { field: "approvalStatus", headerName: "Approved Status", flex: 0.8, minWidth: 140 },
        { field: "lcCalculated", headerName: "LC Calculated", flex: 0.9, minWidth: 140 },
        { field: "new", headerName: "New", flex: 0.6, minWidth: 100 },
      ];

      return detailColumns.map((col) => ({
        ...col,
        editable: col.field === "editNewLandedCost",
      }));
    },
    [onBatchNavigate, variant]
  );

  useEffect(() => {
    if (isEditing) {
      return;
    }
    const normalizedRows = normalizedInput.map((row, index) =>
      variant === "summary"
        ? {
            id: pickValue(row, ["id", "batchJobId", "batchJob_id"], resolveBatchId(row) || index + 1),
            batchId: resolveBatchId(row) || pickValue(row, ["id"], ""),
            batchType: pickValue(row, ["batchType", "batch_type"], ""),
            cjwFilename: resolveCjwFilename(row),
            status: pickValue(row, ["status", "statusName", "status_name"], ""),
            skuStatus: pickValue(row, ["skuStatus", "sku_status"], ""),
            totalNoOfSkus: pickValue(row, ["totalNoOfSkus", "totalSkus", "totalSku", "total_no_of_skus", "total_skus", "total_sku"], null),
            totalOldLandedAmt: pickValue(row, ["totalOldLandedAmt", "totalOldLanded", "total_old_landed_amt", "total_old_landed"], null),
            totalNewLandedAmt: pickValue(row, ["totalNewLandedAmt", "totalNewLanded", "total_new_landed_amt", "total_new_landed"], null),
            totalPercentChgLandedCost: pickValue(row, ["totalPercentChgLandedCost", "totalPercentChangeLandedCost", "total_percent_chg_landed_cost", "total_percent_change_landed_cost"], null),
            createDate: formatDateParts(pickValue(row, ["createDate", "createdDate", "created_at", "createdAt", "create_date"], "")),
          }
        : {
            id: resolveRowId(row, index, resolveBatchId(row, selectedBatchId)),
            batchId: resolveBatchId(row, selectedBatchId),
            parsedSku: pickValue(row, ["parsedSku", "sku", "parsed_sku"], ""),
            hqLine: pickValue(row, ["hqLine", "hq_line"], ""),
            fieldLine: pickValue(row, ["fieldLine", "field_line"], ""),
            asgVendorAccountNumber: pickValue(row, ["asgVendorAccountNumber", "asg_vendor_account_number"], ""),
            asgVendorName: pickValue(row, ["asgVendorName", "asg_vendor_name"], ""),
            asgVendorGroupCode: pickValue(row, ["asgVendorGroupCode", "asg_vendor_group_code"], ""),
            asgVendorProductNumber: pickValue(row, ["asgVendorProductNumber", "asg_vendor_product_number"], ""),
            asgPrimaryVendorAccountNumber: pickValue(row, ["asgPrimaryVendorAccountNumber", "asg_primary_vendor_account_number"], ""),
            productLifeCycleStateName: pickValue(row, ["productLifeCycleStateName", "product_life_cycle_state_name"], ""),
            countryOfOrigin: pickValue(row, ["countryOfOrigin", "country_of_origin"], ""),
            fobCostUnit: pickValue(row, ["fobCostUnit", "fob_cost_unit"], ""),
            unitCostDate: formatDateParts(pickValue(row, ["unitCostDate", "unit_cost_date"], "")),
            palletQuantity: pickValue(row, ["palletQuantity"], ""),
            masterCartonQuantity: pickValue(row, ["masterCartonQuantity"], ""),
            oldFobCost: pickValue(row, ["oldFobCost", "old_fob_cost"], null),
            newFobCost: pickValue(row, ["newFobCost", "new_fob_cost"], null),
            percentChgLandedCost: pickValue(row, ["percentChgLandedCost", "percent_chg_landed_cost"], null),
            oldLandedCost: pickValue(row, ["oldLandedCost", "old_landed_cost"], null),
            newLandedCost: pickValue(row, ["newLandedCost", "new_landed_cost"], null),
            editNewLandedCost: pickValue(
              row,
              ["editNewLandedCost", "edit_new_landed_cost", "newLandedCost", "new_landed_cost"],
              null
            ),
            editPercentChgLandedCost: pickValue(
              row,
              [
                "editPercentChgLandedCost",
                "edit_percent_chg_landed_cost",
                "percentChgLandedCost",
                "percent_chg_landed_cost",
              ],
              null
            ),
            tariffAmt: pickValue(row, ["tariffAmt", "tariff_amt"], null),
            freightAmt: pickValue(row, ["freightAmt", "freight_amt"], null),
            issCommissionAmt: pickValue(row, ["issCommissionAmt", "iss_commission_amt"], null),
            landedCostVariability: pickValue(row, ["landedCostVariability", "landed_cost_variability"], null),
            variabilityPercent: pickValue(row, ["variabilityPercent", "variability_percent"], null),
            adjustmentsRate: pickValue(row, ["adjustmentsRate", "adjustments_rate"], null),
            couterVailingRate: pickValue(row, ["couterVailingRate", "couter_vailing_rate"], null),
            adjustmentsAmt: pickValue(row, ["adjustmentsAmt", "adjustments_amt"], null),
            couterVailingAmt: pickValue(row, ["couterVailingAmt", "couter_vailing_amt"], null),
            containerPalletAmt: pickValue(row, ["containerPalletAmt", "container_pallet_amt"], null),
            poStatusValue: pickValue(row, ["poStatusValue", "po_status_value"], ""),
            poProductNumber: pickValue(row, ["poProductNumber", "po_product_number"], ""),
            areParamsMissing: resolveParamsMissingValue(row),
            approvedDate: formatDateParts(pickValue(row, ["approvedDate", "approved_date"], "")),
            approvedBy: pickValue(row, ["approvedBy", "approved_by"], ""),
            nextLcCalcDate: formatDateParts(pickValue(row, ["nextLcCalcDate", "next_lc_calc_date"], "")),
            repricingStatus: pickValue(row, ["repricingStatus", "repricing_status"], ""),
            eventType: pickValue(row, ["eventType", "event_type"], ""),
            createDate: formatDateParts(pickValue(row, ["createDate", "create_date", "createdDate", "created_at"], "")),
            createdBy: pickValue(row, ["createdBy", "created_by"], ""),
            updateDate: formatDateParts(pickValue(row, ["updateDate", "update_date", "updatedDate", "updated_at"], "")),
            updateBy: pickValue(row, ["updateBy", "update_by"], ""),
            approvalStatus: pickValue(row, ["approvalStatus", "approval_status", "approved", "isApproved"], false),
            lcCalculated: pickValue(row, ["lcCalculated", "isLCCalculated"], false),
            new: pickValue(row, ["new", "isNew"], false),
          }
    );

    setRows(normalizedRows);
  }, [isEditing, normalizedInput, variant, selectedBatchId]);

  const markRowAsDirty = (newRow, oldRow) => {
    if (!newRow || !oldRow) {
      return;
    }
    const changed = Object.keys(newRow).some(
      (key) => newRow[key] !== oldRow[key]
    );
    if (changed) {
      dirtyRowsRef.current.set(newRow.id, newRow);
    } else {
      dirtyRowsRef.current.delete(newRow.id);
    }
  };

  const handleProcessRowUpdate = (newRow, oldRow) => {
    if (!isEditing) {
      return oldRow;
    }
    const rawEditNewLandedCost = newRow?.editNewLandedCost;
    const normalizedEditNewLandedCost = normalizeDecimalValue(rawEditNewLandedCost);
    if (normalizedEditNewLandedCost !== null && !isEditNewLandedCostWithinRange(rawEditNewLandedCost)) {
      AlertManager.error("New landed cost must be between -100000 and 100000.", {
        contactSupport: false,
      });
      return oldRow;
    }
    const nextRow = {
      ...oldRow,
      ...newRow,
      editNewLandedCost: newRow?.editNewLandedCost ?? oldRow?.editNewLandedCost ?? oldRow?.newLandedCost,
      editPercentChgLandedCost:
        newRow?.editPercentChgLandedCost ?? oldRow?.editPercentChgLandedCost ?? oldRow?.percentChgLandedCost,
    };
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === oldRow.id ? nextRow : row))
    );
    markRowAsDirty(nextRow, oldRow);
    return nextRow;
  };

  const commitActiveCellEdits = useCallback(async () => {
    const editModel = apiRef.current?.getEditRowsModel?.() || apiRef.current?.state?.editRows || {};
    const entries = Object.entries(editModel || {});

    for (const [rowId, fields] of entries) {
      const numericId = Number(rowId);
      const resolvedId = Number.isNaN(numericId) ? rowId : numericId;

      for (const field of Object.keys(fields)) {
        try {
          await Promise.resolve(apiRef.current?.commitCellChange?.({ id: resolvedId, field }));
          await Promise.resolve(
            apiRef.current?.stopCellEditMode?.({
              id: resolvedId,
              field,
              ignoreModifications: false,
            })
          );
        } catch (error) {
          console.error("Unable to commit item workflow edit", error);
        }
      }
    }
  }, [apiRef]);

  const performSave = useCallback(async () => {
    if (saving) {
      return;
    }

    const dirtyRows = Array.from(dirtyRowsRef.current.values());
    if (!dirtyRows.length) {
      setIsEditing(false);
      setSubmitArmed(false);
      return;
    }

    try {
      setSaving(true);
      const response = await updateLandedCostItemWorkflow(dirtyRows);
      if (!response) {
        return;
      }
      if (response.ok) {
        dirtyRowsRef.current.clear();
        setIsEditing(false);
        setSubmitArmed(false);
        AlertManager.success("Item workflow updated successfully.", { contactSupport: false });
        if (typeof onRefreshData === "function") {
          await onRefreshData();
        }
        return;
      }
      const errorData = await response.json().catch(() => ({}));
      AlertManager.error(errorData?.message || "Unable to update item workflow.", {
        contactSupport: false,
      });
    } catch (error) {
      AlertManager.error("Unable to update item workflow.", { contactSupport: false });
    } finally {
      setSaving(false);
    }
  }, [onRefreshData, saving]);

  const performApprovalAction = useCallback(
    async (approvedStatus) => {
      if (saving || !selectedRows.length) {
        return;
      }

      if (approvalActionDisabled) {
        AlertManager.error("Selected rows cannot be approved or rejected.", {
          contactSupport: false,
        });
        return;
      }

      try {
        setSaving(true);
        const response = await updateLandedCostItemWorkflow(selectedRows, {
          approved: approvedStatus,
        });
        if (!response) {
          return;
        }
        if (response.ok) {
          setSelectionModel([]);
          AlertManager.success(`Rows marked as ${approvedStatus.toLowerCase()}.`, {
            contactSupport: false,
          });
          if (typeof onRefreshData === "function") {
            await onRefreshData();
          }
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        AlertManager.error(errorData?.message || `Unable to ${approvedStatus.toLowerCase()} rows.`, {
          contactSupport: false,
        });
      } catch (error) {
        AlertManager.error(`Unable to ${approvedStatus.toLowerCase()} rows.`, {
          contactSupport: false,
        });
      } finally {
        setSaving(false);
      }
    },
    [approvalActionDisabled, onRefreshData, saving, selectedRows]
  );

  const handleEditToggle = async () => {
    if (!isEditing) {
      dirtyRowsRef.current.clear();
      setIsEditing(true);
      setSubmitArmed(false);
      return;
    }
    if (saving || !submitArmed) {
      return;
    }
    await commitActiveCellEdits();
    await performSave();
  };

  const handleFinishEditing = async () => {
    await commitActiveCellEdits();
    setSubmitArmed(true);
  };

  const handleApprove = () => performApprovalAction("Approved");

  const handleReject = () => performApprovalAction("Rejected");

  const CustomPagination = () => {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Pagination
        color="primary"
        count={pageCount}
        page={page + 1}
        onChange={(_, value) => apiRef.current.setPage(value - 1)}
      />
    );
  };

  const hideSummaryScroll = variant === "summary" && rows.length <= 5;
  const shouldShowEditControls = variant === "detail" && showActions;
  const rowClassName = useCallback(
    (params) => {
      if (variant === "detail" && hasMissingParams(params?.row)) {
        return "run-details-missing-params-row";
      }
      if (variant === "detail" && isSkuExcluded(params?.row)) {
        return "run-details-sku-excluded-row";
      }
      if (variant === "detail" && isFinalApprovalState(params?.row)) {
        return "run-details-final-approval-row";
      }
      if (variant === "summary" && isNewSkuStatus(params?.row?.skuStatus)) {
        return "run-details-new-status-row";
      }
      return "";
    },
    [variant]
  );

  useEffect(() => {
    if (!showActions && isEditing) {
      dirtyRowsRef.current.clear();
      setIsEditing(false);
      setSubmitArmed(false);
    }
  }, [showActions, isEditing]);

  return (
    <div className={classes.resultsTableCard}>
      {showUploadButton && (
        <Box className={classes.resultsUploadBar}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => onUploadClick && onUploadClick()}
          >
            Upload
          </Button>
        </Box>
      )}
      {shouldShowEditControls && (
        <Box className={classes.resultsActionBar} sx={{ justifyContent: "flex-end", width: "100%" }}>
          <ActionButtonWithTooltip
            title={getEditButtonTooltip()}
            disabled={saving || (isEditing && !submitArmed) || !canEditAnyRow}
            buttonProps={{
              variant: "contained",
              color: "primary",
              size: "small",
              onClick: handleEditToggle,
            }}
          >
            {saving ? "Saving..." : isEditing ? "Save Changes" : "Edit"}
          </ActionButtonWithTooltip>
          {isEditing && (
            <ActionButtonWithTooltip
              title={getEditButtonTooltip()}
              disabled={saving}
              buttonProps={{ variant: "text", size: "small", onClick: handleFinishEditing }}
              buttonClassName={classes.finishEditingBtn}
            >
              Finish Editing
            </ActionButtonWithTooltip>
          )}
          <ActionButtonWithTooltip
            title={getApprovalActionTooltip()}
            disabled={isEditing || saving || !selectedRows.length || approvalActionDisabled}
            buttonProps={{ variant: "contained", color: "success", size: "small", onClick: handleApprove }}
          >
            Approve
          </ActionButtonWithTooltip>
          <ActionButtonWithTooltip
            title={getApprovalActionTooltip()}
            disabled={isEditing || saving || !selectedRows.length || approvalActionDisabled}
            buttonProps={{ variant: "contained", color: "error", size: "small", onClick: handleReject }}
          >
            Reject
          </ActionButtonWithTooltip>
        </Box>
      )}
      <DataGrid
        rows={rows}
        columns={columns}
        key={variant}
        apiRef={apiRef}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        loading={loading}
        autoHeight
        editMode="cell"
        experimentalFeatures={{ newEditingApi: true }}
        checkboxSelection={variant === "detail" && showActions}
        selectionModel={selectionModel}
        onSelectionModelChange={(newSelection) => {
          const ids = Array.isArray(newSelection) ? newSelection : Array.from(newSelection || []);
          setSelectionModel(ids);
        }}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        isRowSelectable={(params) =>
          !hasMissingParams(params?.row) && !isSkuExcluded(params?.row) && !isFinalApprovalState(params?.row)
        }
        isCellEditable={(params) =>
          isEditing &&
          params.field === "editNewLandedCost" &&
          canEditLandedCost(params?.row)
        }
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(error) => console.error(error)}
        pagination
        pageSize={15}
        onPageSizeChange={() => {}}
        rowsPerPageOptions={[15]}
        {...(page !== undefined ? { page } : {})}
        {...(onPageChange ? { onPageChange } : {})}
        hideFooterSelectedRowCount
        components={{ Pagination: CustomPagination }}
        getRowClassName={rowClassName}
        sx={
          {
            ...(hideSummaryScroll
              ? {
                  "& .MuiDataGrid-virtualScroller": {
                    overflowY: "hidden",
                  },
                }
              : {}),
            "& .MuiDataGrid-row.run-details-missing-params-row": {
              backgroundColor: "rgba(211, 47, 47, 0.08)",
            },
            "& .MuiDataGrid-row.run-details-sku-excluded-row": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
            "& .MuiDataGrid-row.run-details-final-approval-row": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
            "& .MuiDataGrid-row.run-details-new-status-row": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
            "& .MuiDataGrid-row.run-details-missing-params-row:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.14)",
            },
            "& .MuiDataGrid-row.run-details-sku-excluded-row:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.06)",
            },
            "& .MuiDataGrid-row.run-details-final-approval-row:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.06)",
            },
            "& .MuiDataGrid-row.run-details-new-status-row:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.06)",
            },
          }
        }
      />
    </div>
  );
};

export default RunDetailsTable;
